import { MCTSMoveGenerationService } from "../../../../utils/MCTSMoveGenerationService";
import {
  GameState,
  Position,
  PieceType,
  IPiece,
  IMove,
} from "../../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  findKing,
} from "../../../../utils/MCTSUtils";
import { DecisionContext, MoveInfo } from "../../../interfaces/DecisionPriority";

/**
 * Monkey Handler
 *
 * This Handler applies special case rules to Monkey interactions
 *
 * BEAR CAPTURES
 *
 * Rules for Monkey king:
 * - If the AI is materially down and there is imminent threat, it can risk a BEAR king capture with a monkey! (66% chance of death!)
 * - If the AI is materially up or equal, it must not risk a BEAR king capture with a monkey king!
 *
 * Rules for monkey (not king)
 * - If the AI is materially down, it must risk a BEAR king capture with a monkey! (66% chance of death!)
 * - If the AI is materially equal, it must risk a BEAR king capture with a monkey!
 * - If the AI is materially up, it must not risk a BEAR king capture with a monkey!
 *
 * MONKEY LEAPS
 *
 * Rules for Monkey king:
 *
 * If there is imminent threat and the leap leads to race victory, it must leap on 1 2 or 3 danger leaps
 * If there is no imminent threat and the leap leads to race victory, it must leap on 1 or 2 danger leaps
 *
 * DEFINITION FOR IMMINENT THREAT
 *
 * Any of the following:
 *
 * Enemy king can race within 2 moves
 * Our King is under current threat in current position
 * Our King has no safe move squares
 * King is especially outnumbered by enemy pieces (Enemy team is at least 2 pieces up)
 */

export interface MonkeyDecision {
  allowBearCapture: boolean;
  allowRiskyLeap: boolean;
  maxLeapDanger: number;
  reasoning: string;
}

export class MonkeyHandler {
  private static instance: MonkeyHandler;

  public static getInstance(): MonkeyHandler {
    if (!MonkeyHandler.instance) {
      MonkeyHandler.instance = new MonkeyHandler();
    }
    return MonkeyHandler.instance;
  }

  /**
   * Main evaluation method for monkey piece decisions
   */
  public evaluateMonkeyMove(
    moveInfo: MoveInfo,
    gameState: GameState,
    isMonkeyKing: boolean = false
  ): MonkeyDecision {
    const piece = getPieceFromPosition(gameState, moveInfo.fromPosition);
    if (!piece || piece.type !== PieceType.Monkey) {
      return {
        allowBearCapture: false,
        allowRiskyLeap: false,
        maxLeapDanger: 0,
        reasoning: "Not a monkey piece",
      };
    }

    const materialStatus = this.getMaterialStatus(gameState);
    const hasImminentThreat = this.hasImminentThreat(gameState);

    // DEBUG: Log material and threat analysis
    const threatDetails = (this as any)._lastThreatAnalysis || [];
    const debugInfo = `🐵 MONKEY EVAL DEBUG: King=${isMonkeyKing}, Material=${
      materialStatus.ourMaterial
    }vs${materialStatus.enemyMaterial} (diff=${
      materialStatus.materialDifference
    }), Down=${
      materialStatus.isMateriallyDown
    }, Threat=${hasImminentThreat} [${threatDetails.join(",")}]`;

    // Check if this move involves capturing a bear
    const targetPiece = getPieceFromPosition(gameState, moveInfo.move.position);
    const isBearCapture = targetPiece?.type === PieceType.Bear;

    // Check if this move leads to race victory
    const leadsToRaceVictory = this.doesMoveLeadToRaceVictory(
      moveInfo,
      gameState
    );

    let allowBearCapture = false;
    let allowRiskyLeap = false;
    let maxLeapDanger = 0;
    let reasoning = debugInfo;

    // BEAR CAPTURE RULES
    if (isBearCapture) {
      if (isMonkeyKing) {
        // Monkey KING bear capture rules
        if (materialStatus.isMateriallyDown && hasImminentThreat) {
          allowBearCapture = true;
          reasoning +=
            " | ✅ Monkey king can risk bear capture - materially down with imminent threat";
        } else {
          allowBearCapture = false;
          reasoning +=
            " | 🚫 Monkey king must not risk bear capture - not materially down or no imminent threat";
        }
      } else {
        // Regular monkey bear capture rules
        if (materialStatus.isMateriallyDown) {
          allowBearCapture = true;
          reasoning +=
            " | ✅ Regular monkey can risk bear capture - materially down";
        } else {
          allowBearCapture = false;
          reasoning +=
            " | 🚫 Regular monkey must not risk bear capture - not materially down";
        }
      }
    }

    // MONKEY LEAP RULES (only for king)
    if (isMonkeyKing && leadsToRaceVictory) {
      const leapDanger = this.calculateLeapDanger(moveInfo, gameState);

      if (hasImminentThreat) {
        // With imminent threat: can leap on 1, 2, or 3 danger leaps
        maxLeapDanger = 3;
        allowRiskyLeap = leapDanger <= 3;
        reasoning +=
          (reasoning ? " | " : "") +
          `Monkey king leap for race victory with imminent threat - max danger 3, actual danger ${leapDanger}`;
      } else {
        // No imminent threat: can leap on 1 or 2 danger leaps
        maxLeapDanger = 2;
        allowRiskyLeap = leapDanger <= 2;
        reasoning +=
          (reasoning ? " | " : "") +
          `Monkey king leap for race victory without imminent threat - max danger 2, actual danger ${leapDanger}`;
      }
    }

    return {
      allowBearCapture,
      allowRiskyLeap,
      maxLeapDanger,
      reasoning: reasoning || "Standard monkey evaluation",
    };
  }

  /**
   * Check if we have imminent threat based on the definition
   */
  private hasImminentThreat(gameState: GameState): boolean {
    const threats = [];

    // 1. Enemy king can race within 2 moves
    if (this.canEnemyKingRaceWithin2Moves(gameState)) {
      threats.push("enemy-king-race");
    }

    // 2. Our King is under current threat in current position
    if (this.isOurKingUnderThreat(gameState)) {
      threats.push("king-under-attack");
    }

    // 3. Our King has no safe move squares
    if (!this.doesOurKingHaveSafeMoves(gameState)) {
      threats.push("no-safe-moves");
    }

    // 4. King is especially outnumbered by enemy pieces (Enemy team is at least 2 pieces up)
    const materialStatus = this.getMaterialStatus(gameState);
    if (materialStatus.materialDifference <= -2) {
      threats.push("heavily-outnumbered");
    }

    const hasAnyThreat = threats.length > 0;

    // Store the threat analysis for debugging
    (this as any)._lastThreatAnalysis = threats;

    return hasAnyThreat;
  }

  /**
   * Get material status for the current player
   */
  private getMaterialStatus(gameState: GameState): {
    isMateriallyDown: boolean;
    materialDifference: number;
    ourMaterial: number;
    enemyMaterial: number;
  } {
    const materialCount = this.getMaterialCount(gameState);
    const isLight = gameState.isLightTurn;

    const ourMaterial = isLight ? materialCount.light : materialCount.dark;
    const enemyMaterial = isLight ? materialCount.dark : materialCount.light;
    const materialDifference = ourMaterial - enemyMaterial;

    return {
      isMateriallyDown: materialDifference < 0,
      materialDifference,
      ourMaterial,
      enemyMaterial,
    };
  }

  /**
   * Count material for both sides (using same logic as Trade priority)
   */
  private getMaterialCount(gameState: GameState): {
    light: number;
    dark: number;
  } {
    let light = 0,
      dark = 0;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          const value = this.getPieceValue(piece.type);
          if (piece.isLight) {
            light += value;
          } else {
            dark += value;
          }
        }
      }
    }

    return { light, dark };
  }

  /**
   * Get piece value (all pieces equal value as per Trade priority)
   */
  private getPieceValue(pieceType: PieceType): number {
    return 1; // All pieces have equal value
  }

  /**
   * Check if enemy king can race within 2 moves
   */
  private canEnemyKingRaceWithin2Moves(gameState: GameState): boolean {
    const isLight = gameState.isLightTurn;
    const enemyKingPosition = findKing(gameState, isLight, true); // Find enemy king

    if (!enemyKingPosition) return false;

    const enemyKing = getPieceFromPosition(gameState, enemyKingPosition);
    if (!enemyKing || !enemyKing.isKing) return false;

    const enemyVictoryRow = isLight ? 0 : 7;
    const currentRow = enemyKingPosition.row;
    const rowsToVictory = Math.abs(currentRow - enemyVictoryRow);

    // Simple heuristic: if within 2-3 rows of victory, consider it a threat
    // This could be enhanced with actual move simulation
    return rowsToVictory <= 2;
  }

  /**
   * Check if our king is under threat in current position
   */
  private isOurKingUnderThreat(gameState: GameState): boolean {
    const isLight = gameState.isLightTurn;
    const ourKingPosition = findKing(gameState, isLight, false);

    if (!ourKingPosition) return false;

    // Create a game state with enemy turn to check if they can attack our king
    const testState = cloneFullGameState(gameState);
    testState.isLightTurn = !isLight;

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(testState);

      // Check if any enemy move can capture our king
      return enemyMoves.some((moveInfo) => {
        const targetPos = moveInfo.move.position;
        return (
          targetPos.row === ourKingPosition.row &&
          targetPos.col === ourKingPosition.col &&
          moveInfo.move.moveType.includes("capture")
        );
      });
    } catch (error) {
      // If we can't generate moves, assume no threat
      return false;
    }
  }

  /**
   * Check if our king has safe moves available
   */
  private doesOurKingHaveSafeMoves(gameState: GameState): boolean {
    const isLight = gameState.isLightTurn;
    const ourKingPosition = findKing(gameState, isLight, false);

    if (!ourKingPosition) return false;

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(gameState);

      // Find all king moves
      const kingMoves = ourMoves.filter((moveInfo) => {
        const piece = getPieceFromPosition(gameState, moveInfo.fromPosition);
        return piece && piece.isKing;
      });

      // Check if any king move leads to a safe square
      for (const kingMove of kingMoves) {
        if (this.isMoveToSafeSquare(kingMove, gameState)) {
          return true;
        }
      }

      return false;
    } catch (error) {
      // If we can't generate moves, assume we have safe moves
      return true;
    }
  }

  /**
   * Check if a move leads to a safe square (not under enemy attack)
   */
  private isMoveToSafeSquare(
    moveInfo: MoveInfo,
    gameState: GameState
  ): boolean {
    // Simulate the move and check if the destination is under attack
    const testState = cloneFullGameState(gameState);
    const piece = getPieceFromPosition(testState, moveInfo.fromPosition);

    if (!piece) return false;

    try {
      // Apply the move
      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      // Check if enemy can attack the new position
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(testState);

      const newPosition = moveInfo.move.position;
      return !enemyMoves.some((enemyMove) => {
        const targetPos = enemyMove.move.position;
        return (
          targetPos.row === newPosition.row &&
          targetPos.col === newPosition.col &&
          enemyMove.move.moveType.includes("capture")
        );
      });
    } catch (error) {
      // If simulation fails, assume it's safe
      return true;
    }
  }

  /**
   * Check if a move leads to race victory
   */
  private doesMoveLeadToRaceVictory(
    moveInfo: MoveInfo,
    gameState: GameState
  ): boolean {
    const isLight = gameState.isLightTurn;
    const victoryRow = isLight ? 7 : 0;

    return moveInfo.move.position.row === victoryRow;
  }

  /**
   * Calculate leap danger level (simplified for now)
   * This could be enhanced to actually count threatening enemy pieces
   */
  private calculateLeapDanger(
    moveInfo: MoveInfo,
    gameState: GameState
  ): number {
    // For now, return a simple danger level based on position
    // This could be enhanced to count actual threats
    const targetPos = moveInfo.move.position;
    const isLight = gameState.isLightTurn;

    // Simple heuristic: danger increases closer to enemy territory
    if (isLight) {
      // Light moving toward row 7, danger increases with higher rows
      return Math.max(1, Math.floor(targetPos.row / 2));
    } else {
      // Dark moving toward row 0, danger increases with lower rows
      return Math.max(1, Math.floor((7 - targetPos.row) / 2));
    }
  }
}


